// Dependencies
import { Component } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { connect } from 'react-redux';

import { bindActionCreators, Dispatch } from 'redux';
import { searchIds } from 'services/search';
import { SetErrorHandlerAction, setErrorHandlerAction } from 'store/actions/errorsActions';
import { SetHighlightObjectAction, setHighlightObjectAction } from 'store/actions/layoutActions';
import { PortType, RootState } from 'types';
import { MainControlsInterface, SearchControlsInterface } from 'types/functions';
import { FilterObjectType } from 'types/search';
import { SummaryData } from 'types/system';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { checkDifferent } from 'utils/formHandlers';
import { formatFilterObject, formatSearchNumbers } from 'utils/migrations';
import { twclsx } from 'utils/twclsx';

import { PortState } from 'store/reducers/portReducer';
import Filter from '../filter/Filter';
import ResultMessage from '../filter/ResultMessage';

interface HighlightTabComponentProps {
    active: boolean;

    highlightObject: FilterObjectType;
    searchObject: FilterObjectType;
    setHighlightObjectAction: SetHighlightObjectAction;
    port: PortType;
    setErrorHandlerAction: SetErrorHandlerAction;
    mainControls: MainControlsInterface;
    searchControls: SearchControlsInterface;
    dateTimeFormat: DateTimeFormat;
    portData: PortState;
}

interface HighlightTabComponentState {
    active: boolean;

    highlightObject: FilterObjectType;
    loading: boolean;
    highlightNumbers: SummaryData | null;
    searchNumbers: SummaryData | null;
}

class HighlightTab extends Component<HighlightTabComponentProps, HighlightTabComponentState> {
    constructor(props: HighlightTabComponentProps) {
        super(props);
        this.state = {
            active: props.active,
            loading: false,
            highlightNumbers: null,
            searchNumbers: null,
            highlightObject: props.highlightObject,
        };
    }

    static getDerivedStateFromProps(props: HighlightTabComponentProps, state: HighlightTabComponentState) {
        if (checkDifferent(props.highlightObject, state.highlightObject) || props.active !== state.active) {
            return {
                highlightObject: props.highlightObject,
                active: props.active,
            };
        }
        return null;
    }

    componentDidUpdate(prevProps: HighlightTabComponentProps, prevState: HighlightTabComponentState) {
        if (checkDifferent(prevState.active, this.state.active)) {
            this.setState({
                searchNumbers: this.props.searchControls.getSearchObjectNumbers(),
            });
        }
        if (checkDifferent(prevState.highlightObject, this.state.highlightObject)) {
            if (checkDifferent(this.state.highlightObject, this.props.searchObject)) {
                console.log('XHR : Highlight object updtaed , ready to get numbers ');
                this.runGetSearchNumbers();
            } else {
                this.setState({
                    highlightNumbers: null,
                    searchNumbers: this.props.searchControls.getSearchObjectNumbers(),
                });
                //only if the search object is not loaded
                if (!this.props.searchObject.uuid) {
                    this.props.mainControls.updateHighlightAll([], true, false);
                }
            }
        }
    }

    runGetSearchNumbers = () => {
        if (
            !checkDifferent(this.state.highlightObject, {
                ...this.props.searchObject,
                //@ts-ignore
                status: this.state.highlightObject?.status,
            })
        ) {
            return;
        }

        this.setState({ loading: true }, () => {
            searchIds(
                'highlight',
                this.props.port.id,
                formatFilterObject(this.state.highlightObject, this.props.port.backEnd?.version, this.props.portData)
            )
                .then((res) => {
                    const numbers = formatSearchNumbers(res, this.props.port.backEnd?.version);

                    this.setState(
                        {
                            loading: false,
                            searchNumbers: this.props.searchControls.getSearchObjectNumbers(),
                            highlightNumbers: {
                                total: numbers.total,
                                inDateRangeForReview: numbers.dateRangeForReview,
                                inDateRange: numbers.dateRange,
                                forReview: numbers.forReview,
                                ppu: numbers.ppu,
                                simulated: numbers.simulated,
                                endDate: this.props.dateTimeFormat.parseDateTime(numbers.endDate, 'highlight end date'),
                                startDate: this.props.dateTimeFormat.parseDateTime(
                                    numbers.startDate,
                                    'highlight start date'
                                ),
                                time: this.props.dateTimeFormat.parseDateTime(null, 'highlight exceute time'),
                            },
                        },
                        () => {
                            this.props.mainControls.updateHighlightAll(
                                this.props.port.backEnd?.version !== 2 ? res.items : res.transits,
                                true,
                                false
                            );
                        }
                    );
                })
                .catch((error) => {
                    this.props.setErrorHandlerAction(error);
                });
        });
    };

    render() {
        return (
            <div className={twclsx(!this.state.active && 'hidden')}>
                <ResultMessage
                    display="highlight"
                    limit={this.state.highlightObject.limit}
                    isLoading={this.state.loading}
                    ishighlightResults={this.state.highlightNumbers !== null}
                    highlightNumbers={this.state.highlightNumbers}
                ></ResultMessage>
                <Scrollbars
                    className={twclsx('h-highlight', 'w-full', 'pr-1', 'mt-2')}
                    renderThumbVertical={({ style, ...props }) => (
                        <div
                            {...props}
                            style={{
                                ...style,
                                background: '#0000004a',
                                borderRadius: 5,
                                zIndex: 30, // Custom z-index
                                position: 'relative', // Required for z-index to work
                            }}
                        />
                    )}
                >
                    <Filter
                        defaultData={this.props.searchObject}
                        type="highlight"
                        autoApply={true}
                        seFilterObject={this.props.setHighlightObjectAction}
                        filterObject={this.state.highlightObject}
                    ></Filter>
                </Scrollbars>
            </div>
        );
    }
}

const highlightTabStateToProps = (state: RootState) => ({
    port: state.port.port,
    portData: state.port,
    mainControls: state.layout.mainControls,
    dateTimeFormat: state.port.dateTimeFormat,
    searchControls: state.layout.searchControls,
});

const highlightTabDispatchToProps = (dispatch: Dispatch) => ({
    setHighlightObjectAction: bindActionCreators(setHighlightObjectAction, dispatch),
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
});

export default connect(highlightTabStateToProps, highlightTabDispatchToProps)(HighlightTab);
